import { createContext, useContext, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Define locale-specific content types
interface LocaleContent {
  currency: string
  dateFormat: string
  numberFormat: {
    decimalSeparator: string
    thousandSeparator: string
    currencyPrecision: number
  }
  localeSpecificContent: {
    [key: string]: any
  }
}

// Default content configuration for each locale
const localeConfig: Record<string, LocaleContent> = {
  it: {
    currency: 'EUR',
    dateFormat: 'DD/MM/YYYY',
    numberFormat: {
      decimalSeparator: ',',
      thousandSeparator: '.',
      currencyPrecision: 2
    },
    localeSpecificContent: {
      // Add any Italian-specific content here
    }
  },
  en: {
    currency: 'EUR',
    dateFormat: 'MM/DD/YYYY',
    numberFormat: {
      decimalSeparator: '.',
      thousandSeparator: ',',
      currencyPrecision: 2
    },
    localeSpecificContent: {
      // Add any English-specific content here
    }
  },
  es: {
    currency: 'EUR',
    dateFormat: 'DD/MM/YYYY',
    numberFormat: {
      decimalSeparator: ',',
      thousandSeparator: '.',
      currencyPrecision: 2
    },
    localeSpecificContent: {
      // Add any Spanish-specific content here
    }
  }
}

// Create context with default value
const LocaleContentContext = createContext<LocaleContent>(localeConfig.it)

// Provider component
interface LocaleContentProviderProps {
  children: ReactNode
}

export const LocaleContentProvider = ({ children }: LocaleContentProviderProps) => {
  const { i18n } = useTranslation()
  const [config, setConfig] = useState<LocaleContent>(localeConfig.it)

  useEffect(() => {
    const updateConfig = () => {
      const currentLocale = i18n.language || 'it'
      // Get config for current language, fallback to 'it' if not found
      const newConfig = localeConfig[currentLocale] || localeConfig.it
      setConfig(newConfig)
    }

    // Update config when language changes
    updateConfig()
    // i18n.on returns void in the type definitions; cast to handle the unsubscribe
    // callback that some i18next versions return at runtime
    const unsubscribe = i18n.on('languageChanged', updateConfig) as unknown as (() => void) | undefined

    // Cleanup the i18n listener if it was returned
    return () => {
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe()
        }
      } catch (error) {
        console.warn('Error unsubscribing from i18n language changes:', error)
      }
    }
  }, [i18n])

  return (
    <LocaleContentContext.Provider value={config}>
      {children}
    </LocaleContentContext.Provider>
  )
}

// Custom hook to use locale-specific content
export const useLocaleContent = () => {
  const context = useContext(LocaleContentContext)
  if (!context) {
    throw new Error('useLocaleContent must be used within a LocaleContentProvider')
  }
  return context
}